import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface UseTodoListMoveReturn {
  moveItem: (vars: {
    itemId: string;
    targetListId: string;
    targetSectionId?: string | null;
  }) => Promise<unknown>;
  moveSection: (vars: {
    sectionId: string;
    targetListId: string;
    targetParentSectionId?: string | null;
  }) => Promise<unknown>;
  moving: boolean;
}

/**
 * Cross-list move mutations. Re-homes a task (with its sub-tasks) or a whole
 * section subtree into another list in the same workspace. Both touch two lists,
 * so success invalidates the whole todo-lists tree (source + destination). Toasts
 * on success/failure. Owner/admin only — the server re-enforces the role.
 */
export function useTodoListMove(holderId: string | null): UseTodoListMoveReturn {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.all });

  const moveItemMut = useMutation({
    mutationFn: (vars: { itemId: string; targetListId: string; targetSectionId?: string | null }) =>
      api.moveTodoItemToList(holderId as string, vars.itemId, {
        targetListId: vars.targetListId,
        targetSectionId: vars.targetSectionId ?? null,
      }),
    onSuccess: () => {
      void invalidateAll();
      toast({ title: 'Task moved' });
    },
    onError: () => toast({ title: 'Couldn’t move the task', variant: 'destructive' }),
  });

  const moveSectionMut = useMutation({
    mutationFn: (vars: {
      sectionId: string;
      targetListId: string;
      targetParentSectionId?: string | null;
    }) =>
      api.moveTodoSectionToList(holderId as string, vars.sectionId, {
        targetListId: vars.targetListId,
        targetParentSectionId: vars.targetParentSectionId ?? null,
      }),
    onSuccess: (res) => {
      void invalidateAll();
      const n = res.movedItemCount;
      toast({
        title: n > 0 ? `Section moved · ${n} task${n === 1 ? '' : 's'}` : 'Section moved',
      });
    },
    onError: () => toast({ title: 'Couldn’t move the section', variant: 'destructive' }),
  });

  return {
    moveItem: (vars) => moveItemMut.mutateAsync(vars),
    moveSection: (vars) => moveSectionMut.mutateAsync(vars),
    moving: moveItemMut.isPending || moveSectionMut.isPending,
  };
}
